import { useCallback, useEffect, useState } from 'react'
import { useStoreImportacion } from './store/store-importacion'
import { Box, Typography } from '@mui/material'
import DescripcionPaso from './components/DescripcionPaso'
import { GruposAtributos, GruposList, ProductoAtributoList } from '../types/responses'
import { AtributosEquivalencias, SaveValores, ValoresList } from '../types'
import { AsignarAtributosProps, RubroYValores } from './types/atributos'
import { firstLetterUpperCase } from '../../../utils/utils'
import LinearProgressWithLabel from '../../../components/LinearProgressWithLabel'
import Loading from '../../../components/Common/Loading'
import { attributes, eliminarDuplicados } from './utils/helpers'
import AsignarAtributoImpo from './AsignarAtributoImpo'
import useRubros from '../../Products/hooks/rubros.hook'

interface Props extends AsignarAtributosProps {
  setValoresASincronizar: React.Dispatch<React.SetStateAction<SaveValores[]>>
  atributosValores: AtributosEquivalencias[]
}

const AsignarAtributos = ({
  gruposAtr,
  paso,
  atributos,
  equivalencias,
  valoresList,
  setValoresList,
  setEquivalencias,
  handleNext,
  setValoresASincronizar,
  atributosValores
}: Props) => {
  const { loading, percentage } = useStoreImportacion((state) => state)
  const { flatRubros, loading: loadingRubros } = useRubros()
  const [RubroYValores, setRubroYValores] = useState<RubroYValores[]>([])

  const handleChange = useCallback(
    (attrName: string, value: ValoresList, codRubro: string, attrsRubro?: ProductoAtributoList) => {
      setValoresASincronizar((prev) => {
        const found = prev.find(
          (p) => p.idIntegracionAtributo === value.id && p.nombreAtributoValorExterno === attrName
        )
        if (found) {
          found.nombreAtributoValorExterno = attrName
          found.idProductoAtributo = attrsRubro.idProductoAtributo
          found.idIntegracionAtributo = 0
        } else {
          prev.push({
            idProductoAtributoValorNormalizado: value.id,
            nombreAtributoValorExterno: attrName,
            idIntegracionAtributo: 0,
            idProductoAtributo: attrsRubro.idProductoAtributo
          })
        }

        return prev
      })
      const newEquivalencias = {
        ...equivalencias,
        [attrName]: {
          id: value.id,
          value: value.nombre,
          no_importar: false,
          codRubro: codRubro
        }
      }

      setEquivalencias(newEquivalencias)

      const newValue = {
        id: value.id,
        nombre: value.nombre,
        atributo: attrsRubro.nombrePlural.toLowerCase(),
        equivalencia: attrName
      }

      let newValues = {}

      if (valoresList[codRubro]) {
        const found = valoresList[codRubro].find((v: ValoresList) => v.equivalencia === attrName)
        if (found) {
          newValues = {
            ...valoresList,
            [codRubro]: valoresList[codRubro].map((v: ValoresList) => (v.equivalencia === attrName ? newValue : v))
          }
        } else {
          newValues = {
            ...valoresList,
            [codRubro]: [...valoresList[codRubro], newValue]
          }
        }
      } else {
        newValues = {
          ...valoresList,
          [codRubro]: [newValue]
        }
      }

      setValoresList(newValues)
    },
    [setEquivalencias, setValoresList, valoresList, equivalencias]
  )

  useEffect(() => {
    if (!flatRubros.length || !atributos || Object.keys(atributos).length === 0 || RubroYValores.length > 0) return

    const nuevosRubroYValores: RubroYValores[] = []

    for (const a of atributos) {
      for (const key in attributes) {
        const { singular, plural } = attributes[key]
        const codRubro = Object.keys(a)[0]
        const rubro = flatRubros.find((r) => r.codRubro?.toString() === codRubro)
        if (!rubro) continue

        const gruposList: GruposList[] = gruposAtr
          .flatMap((g: GruposAtributos) => g.grupos)
          .filter((grupo: GruposList) =>
            rubro?.atributosPermitidos?.some((a) => a.gruposPermitidos?.includes(grupo.idGrupo))
          )
        const attribute = rubro?.atributosPermitidos.find((a) => a.slug.toLowerCase() === singular.toLowerCase())
        if (!attribute) continue

        const attrs = Object.values(a)[0][key]
        /* const valores = attrs
          .map((nombre) => attribute.gruposList.find((v) => v.nombre === nombre))
          .filter((v): v is GruposList => !!v) */

        nuevosRubroYValores.push({
          rubro: {
            codRubro: rubro.codRubro,
            nombre: rubro.nombreAnalytics
          },
          atributosDefault: {
            key,
            all: attribute,
            valores: gruposList
          },
          atributosParaAsignar: attrs
        })
      }
    }

    if (!nuevosRubroYValores.length) return

    const valoresListInitial = { ...valoresList }

    nuevosRubroYValores.forEach((r) => {
      const { atributosDefault, atributosParaAsignar, rubro } = r
      const key = atributosDefault.key
      const codRubro = rubro.codRubro

      atributosParaAsignar.forEach((a) => {
        const yaExiste = valoresListInitial[codRubro]?.some((v) => v.equivalencia === a)
        if (!yaExiste) {
          if (!valoresListInitial[codRubro]) valoresListInitial[codRubro] = []
          valoresListInitial[codRubro].push({
            id: -1,
            nombre: a,
            atributo: key,
            equivalencia: a
          })
        }
      })
    })

    // console.log('[RubroYValores FINAL]', JSON.stringify(nuevosRubroYValores, null, 2))

    setRubroYValores(nuevosRubroYValores)
    setValoresList(eliminarDuplicados(valoresListInitial))
  }, [atributos, flatRubros])

  if (loading || loadingRubros)
    return percentage !== null ? <LinearProgressWithLabel value={percentage} /> : <Loading />

  if (RubroYValores.length === 0) return <Typography variant="h6">No hay atributos para asignar</Typography>

  return (
    <Box display="flex" flexDirection="column" gap="16px" width="100%">
      {/* Descripción */}
      <DescripcionPaso props={{ my: 2 }}>
        Seleccioná cuáles son los valores de atributos de tu catálogo para que se correspondan con los que usamos en
        Avellaneda a un Toque
      </DescripcionPaso>

      {/* Screen Atributos por paso */}
      <Box maxHeight="400px" overflow="auto">
        <Typography variant="h6" fontWeight={'bold'}>
          {firstLetterUpperCase(paso)}
        </Typography>
        {RubroYValores.map((r, i) => (
          <AsignarAtributoImpo
            key={i}
            rubro={r}
            paso={paso}
            handleChange={handleChange}
            handleNext={handleNext}
            equivalencias={equivalencias}
            setEquivalencias={setEquivalencias}
            atributosValores={atributosValores}
          />
        ))}
      </Box>
    </Box>
  )
}

export default AsignarAtributos
